// budget.ts — Patch 015.6. SCS budget-to-actual methods.
//
// scs-lines CRUD + the budget-to-actual rollup that joins draw_invoices
// (010.C) on scs_line_id. See workers/core-os/src/routes/scs.ts.

import { request } from './request'
import type {
  BudgetSummaryResponse,
  CreateScsLineInput,
  PatchScsLineInput,
  ScsLinesResponse,
} from './types'

// --- Forward bid->budget money flow (2026-06-01) -----------------------------
// budget_lines is the bid-formed actual budget (the award handler writes one
// per award; manual lines like the GC fee are added here). Distinct from the
// scs_lines planning estimate above.

export interface BudgetLineRow {
  id: string
  display_name: string
  category: string | null
  linked_sow_id: string | null
  budgeted_cents: number
  committed_cents: number
  funded_cents: number
  notes: string | null
  created_at: string
  trade: string | null
  contractor_display_name: string | null
}

export interface BudgetLinesResponse {
  ok: true
  lines: BudgetLineRow[]
  totals: { budgeted_cents: number; committed_cents: number; funded_cents: number }
  count: number
}

export interface CreateBudgetLineInput {
  display_name: string
  category?: string
  budgeted_cents: number
  notes?: string
}

export const budgetApi = {
  // Budget-to-actual rollup: per SCS line + project totals.
  summary: (projectId: string) =>
    request<BudgetSummaryResponse>(
      `/api/projects/${encodeURIComponent(projectId)}/budget`,
    ),
  listScsLines: (projectId: string) =>
    request<ScsLinesResponse>(
      `/api/projects/${encodeURIComponent(projectId)}/scs-lines`,
    ),
  createScsLine: (projectId: string, body: CreateScsLineInput) =>
    request<{ ok: true; id: string; line_number: number; request_id: string }>(
      `/api/projects/${encodeURIComponent(projectId)}/scs-lines`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  updateScsLine: (projectId: string, scsLineId: string, body: PatchScsLineInput) =>
    request<{ ok: true; id: string; updated: string[] }>(
      `/api/projects/${encodeURIComponent(projectId)}/scs-lines/${encodeURIComponent(
        scsLineId,
      )}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
  // Forward bid-formed budget (budget_lines).
  listBudgetLines: (projectId: string) =>
    request<BudgetLinesResponse>(
      `/api/projects/${encodeURIComponent(projectId)}/budget-lines`,
    ),
  createBudgetLine: (projectId: string, body: CreateBudgetLineInput) =>
    request<{ ok: true; id: string; request_id: string }>(
      `/api/projects/${encodeURIComponent(projectId)}/budget-lines`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
}
